/* eslint-disable */
import type { LocalizableString } from "../../cash/localization/localization.pb";
import type { Color } from "../../cash/ui/Color.pb";
import type { Icon } from "../../cash/ui/Icon.pb";
import type { Image } from "../../cash/ui/Image.pb";

export interface UiAvatar {
  /**
   * The accent color to use as the background of the avatar
   * Used together with monogram, icon.
   * If not specified, the client will fall back to using the Gray 85 color, #D9D9D9.
   * If you want a transparent background for an image, you must specify a clear background color.
   * Optional.
   */
  background_color?: Color;
  /**
   * The monogram text to display in the middle of the avatar, currently only one character is allowed.
   * Display priority: image > monogram_text > icon
   * Optional.
   */
  monogram_text?: string;
  /**
   * The arcade icon to display in the middle of the avatar
   * Display priority: image > monogram_text > icon
   * Optional.
   */
  icon?: Icon;
  /**
   * Tint color for the image, monogram_text or icon
   * If not specified, the client will fall back to using Semantic.Text.inverse color, #FFFFFF.
   * Optional.
   */
  tint_color?: Color;
  /**
   * The color to use as the border of the avatar
   * Used together with image. If not specified, the client will not display the image with a border.
   * Optional.
   */
  border_color?: Color;
  /**
   * The accessibility value to apply on the client.
   * Most likely the display name for the entity that the avatar represents.
   * Optional.
   */
  accessibility_value?: string;
  localizable_accessibility_value?: LocalizableString;
  /**
   * Light- and dark-themed URLs for the photo to display in the avatar
   * Display priority: image > monogram_text > icon
   * Optional.
   */
  image?: Image;
  /**
   * Shape to draw the background behind the content
   * This field is experimental
   */
  shape?: UiAvatarShape;
  /**
   * How to scale `image` within the shape. If not specified, the client uses FILL.
   * Clients that don't support this field fall back to FILL.
   * This field is experimental
   */
  image_scale?: UiAvatarImageScale;
  /**
   * True if `image` is a template image and should be colorized/tinted.
   * `background_color` will also be shown when this is true. `background_color` will not be shown with `image` if this
   * is false or null.
   * Available in client versions >= 3.53.
   */
  is_template_avatar?: boolean;
  /**
   * The remote badge image, available only to clients on version 3.56 and above.
   * Display priority: badge_icon > badge_image
   * badge_image should only be provided for one of two cases:
   * 1. provided along with badge_icon, for backward compatibility for pre-arcade client apps
   * 2. provided without badge_icon, due to icon is special and not available in Arcade icon library
   * Optional.
   */
  badge_image?: Image;
  /**
   * Provided if badge is an arcade icon
   * Display priority: badge_icon > badge_image
   * Optional.
   */
  badge_icon?: Icon;
  badge_shape?: UiAvatarBadgeShape;
  /**
   * Optional. Will use the default if not set.
   * Only applies when using badge_icon.
   * The color of the icon will adjust to maximise contrast over the color
   * TODO: Add client version support https://linear.app/squareup/issue/ARC-164/allow-avatar-badges-to-support-changing-the-background-color
   */
  badge_background_color?: Color;
}

export enum UiAvatarBadgeShape {
  /** CIRCULAR - Circular shape, which is the default. */
  CIRCULAR = "CIRCULAR",
  /** CARD - A card shape, used for gift cards. */
  CARD = "CARD",
}

export enum UiAvatarShape {
  /** CIRCLE - Circle shape, which is the default */
  CIRCLE = "CIRCLE",
  /** SQUIRCLE - Square with rounded corners */
  SQUIRCLE = "SQUIRCLE",
}

export enum UiAvatarImageScale {
  /** FILL - Scale the image to fill the shape, cropping whatever falls outside it. This is the default. */
  FILL = "FILL",
  /**
   * FIT - Scale the image down so all of it shows inside the shape, with the background behind it.
   * Used for images that lose meaning when cropped, like gift card art.
   */
  FIT = "FIT",
}
